import { ErrorCode } from '@fluenceur/types';
import type { InvitationView } from '@/stores/invitation-store';

const SEND = 'invitations.modal.collab.wizard.send';
const DONE = 'invitations.modal.collab.wizard.done';

/**
 * Send refusals a screen of this wizard can settle: the step that owns the
 * control, and what the brand is told on the way there.
 *
 * The terms step owns every term of the deal; the channel step owns the brand's
 * own legal identity, because that is where the banner and the link to the
 * settings live — no term of the brief would fix a missing registration number.
 * The delivery step owns the proposal email.
 */
export const SEND_REFUSALS: Partial<Record<string, { view: InvitationView; message: string }>> = {
  [ErrorCode.E_COLLAB_COMPENSATION_REQUIRED]: {
    view: 'collabTerms',
    message: `${SEND}.compensationRequiredError`,
  },
  [ErrorCode.E_COLLAB_USAGE_RIGHTS_REQUIRED]: {
    view: 'collabTerms',
    message: `${SEND}.usageRightsRequiredError`,
  },
  [ErrorCode.E_COLLAB_GIFTED_PRODUCT_VALUE_REQUIRED]: {
    view: 'collabTerms',
    message: `${SEND}.productValueRequiredError`,
  },
  [ErrorCode.E_COLLAB_BRAND_IDENTITY_INCOMPLETE]: {
    view: 'collabSend',
    message: `${SEND}.brandIdentityRequiredError`,
  },
  // A brief nobody funded may be proposed again once. Nothing in the terms
  // undoes a second attempt, so the brand is put back where the recipient and
  // the channel are settled, and told plainly.
  [ErrorCode.E_COLLAB_REPROPOSAL_LIMIT]: {
    view: 'collabSend',
    message: `${SEND}.reproposalLimitError`,
  },
  // The channel step is where the mailbox is connected, and where the plan
  // offer stands when the plan masks the address.
  [ErrorCode.E_COLLAB_MAILBOX_NOT_CONNECTED]: {
    view: 'collabSend',
    message: `${SEND}.mailboxNotConnectedError`,
  },
  [ErrorCode.E_PLAN_CREATOR_CONTACT_LOCKED]: {
    view: 'collabSend',
    message: `${SEND}.contactLockedError`,
  },
  [ErrorCode.E_COLLAB_PROPOSAL_LINK_MISSING]: {
    view: 'collabDelivery',
    message: `${SEND}.proposalLinkMissingError`,
  },
};

/** Refusals no step can fix, still told in the brand's language on the final screen. */
const LINE_ONLY_REFUSALS: Partial<Record<string, string>> = {
  [ErrorCode.E_CREATOR_OPPORTUNITY_UNAVAILABLE]: `${DONE}.failureOpportunityUnavailable`,
};

/**
 * The translation key for a refused recipient. The server's English message is
 * never shown: a code nobody translated yet reads as a generic refusal.
 */
export const refusalMessageKey = (code: string | null): string =>
  (code && (SEND_REFUSALS[code]?.message ?? LINE_ONLY_REFUSALS[code])) || `${DONE}.failureUnknown`;
